import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * The fan: three slabs that travel out of a circle over a scrim, and back into it.
 * One implementation for the rail's + (`stories/CreateRing`, fans down-right under the
 * navbar) and the bottom nav's HOST (`MobileTabBar`, fans up-right toward the thumb).
 * Bottom-nav v2 handoff (Oct 2026) — the motion is CreateRing's, unchanged.
 *
 * The scrim and the slabs render in a PORTAL off the anchor's own rect: the rail is a
 * horizontal scroller that clips vertically, and the tab bar is `pointer-events: none`
 * chrome. Fixed coordinates keep the geometry exact while letting the fan escape.
 *
 * The accent is the border and the label, never the fill. Magenta is deliberately absent:
 * it means LIVE and Party Starter, and a third meaning would make all three mean less.
 */

/** Every slab anything can offer. A label lives here once; both fans read it. */
export const FAN_ITEMS = {
  portal: { key: 'portal', label: 'OPEN A PORTAL', accent: '#00FFFF', to: '/portals/new' },
  event: { key: 'event', label: 'CREATE AN EVENT', accent: '#EAB308', to: '/host/events/create' },
  party: { key: 'party', label: 'PLAN AN EVENT', accent: '#C6B8FF', to: '/host/events/plan' },
  club: { key: 'club', label: 'START A CLUB', accent: '#C6B8FF', to: '/clubs/create' },
  dashboard: { key: 'dashboard', label: 'HOST DASHBOARD', accent: '#C6B8FF', to: '/host' },
};

/**
 * Where the slabs go, by origin, relative to the anchor's top-left — in the handoff's units
 * (a 64px cell for the rail, the 50px circle for the tab bar). `closed` is just inside the
 * circle, so they collapse into it. `open` is a list, or a function of the slab index for a
 * fan whose count varies (the voice dock's skip steps: three or five, fanning down-right
 * from a 40px button at the top of the screen — voice-notes handoff §1).
 */
export const FAN_GEOMETRY = {
  rail: { closed: { left: 4, top: 10 }, open: [{ left: 76, top: 4 }, { left: 60, top: 64 }, { left: 20, top: 124 }] },
  tabbar: { closed: { left: -51, top: 3 }, open: [{ left: 8, top: -62 }, { left: 28, top: -118 }, { left: 48, top: -174 }] },
  skip: { width: 108, height: 44, stagger: 60, closed: { left: 0, top: 0 }, open: (i) => ({ left: 8 + 10 * i, top: 52 + 52 * i }) },
};

export const SLAB_WIDTH = 148;
export const OPEN_MS = 180;
export const CLOSE_MS = 120;
export const STAGGER_MS = 80;

export function prefersReducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}

/** The + that turns into an × — on the face of both anchors. */
export const FanPlusGlyph = ({ rotated, reduced, size = 22, strokeWidth = 2 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="square" strokeLinejoin="miter"
    style={{
      transform: `rotate(${rotated && !reduced ? 45 : 0}deg)`,
      transition: reduced ? 'none' : `transform ${OPEN_MS}ms cubic-bezier(0,0,0.2,1)`,
    }}
  >
    <path d="M5 12h14M12 5v14" />
  </svg>
);

/**
 * The fan's state: mount → next frame expand → close → unmount after the travel. Esc,
 * scroll and resize (after a grace period) close it; focus stays inside while open.
 *
 *   returns { mounted, expanded, rect, reduced, open(anchorEl?), close(returnFocus),
 *             onKeyDownInFan, buttonRef, slabRefs }
 */
export function useFan({ onOpenChange = null } = {}) {
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [rect, setRect] = useState(null);
  const buttonRef = useRef(null);
  const slabRefs = useRef([]);
  const closeTimer = useRef(null);
  const flipTimer = useRef(null);
  const openedAt = useRef(0);
  const reduced = prefersReducedMotion();

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (flipTimer.current) clearTimeout(flipTimer.current);
  }, []);
  useEffect(() => { onOpenChange?.(expanded); }, [expanded, onOpenChange]);

  const close = useCallback((returnFocus = true) => {
    if (flipTimer.current) clearTimeout(flipTimer.current);
    setExpanded(false);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMounted(false), reduced ? 0 : CLOSE_MS);
    if (returnFocus) buttonRef.current?.focus();
  }, [reduced]);

  // `anchorEl` is what the slabs measure from when it isn't the focused button itself (the
  // tab bar's circle sits above its button).
  const open = useCallback((anchorEl = null) => {
    const r = (anchorEl || buttonRef.current)?.getBoundingClientRect();
    if (!r) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setRect({ left: r.left, top: r.top });
    setMounted(true);
    openedAt.current = Date.now();

    // Render one frame at the closed coordinates first, so the slabs have somewhere to travel
    // FROM. rAF is the right signal for that — except a backgrounded tab never fires it, and a
    // menu that stays invisible until you look at the tab is worse than one that skips its
    // animation. So: whichever of the two fires first wins, once.
    let flipped = false;
    const flip = () => { if (flipped) return; flipped = true; setExpanded(true); };
    requestAnimationFrame(() => requestAnimationFrame(flip));
    flipTimer.current = setTimeout(flip, 32);
  }, []);

  /** Pick: the fan goes away at once; the caller navigates. */
  const dismiss = useCallback(() => {
    if (flipTimer.current) clearTimeout(flipTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setExpanded(false);
    setMounted(false);
  }, []);

  // Esc closes. So does scrolling or resizing — the fan is pinned to where the anchor WAS,
  // and following it around the page is worse than putting it away.
  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    // Focusing the anchor can scroll it into view — a scroll the person didn't do. Anything
    // in the first moments after opening is that, not a dismissal.
    const onMove = () => { if (Date.now() - openedAt.current > 250) close(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, { capture: true });
      window.removeEventListener('resize', onMove);
    };
  }, [mounted, close]);

  // Focus stays inside the fan while it's open: anchor → slab 1 → 2 → 3 → back. React events
  // bubble through the portal, so a handler on the anchor's wrapper catches the slabs too.
  const onKeyDownInFan = (e) => {
    if (e.key !== 'Tab' || !expanded) return;
    const order = [buttonRef.current, ...slabRefs.current].filter(Boolean);
    const i = order.indexOf(document.activeElement);
    if (i === -1) return;
    const next = e.shiftKey ? (i - 1 + order.length) % order.length : (i + 1) % order.length;
    e.preventDefault();
    order[next].focus();
  };

  return { mounted, expanded, rect, reduced, open, close, dismiss, onKeyDownInFan, buttonRef, slabRefs };
}

/**
 * The portalled part: scrim, slabs, and the anchor's inverted face on top of the scrim.
 *
 *   fan         from useFan
 *   items       three of FAN_ITEMS — or any `{ key, label, accent }` (the skip fan's steps)
 *   origin      'rail' | 'tabbar' | 'skip' — which geometry
 *   scale       the rail draws at 64 in the handoff and 72 on desktop
 *   face        the inverted anchor to draw at the anchor's rect (a node)
 *   anchorSize  width/height of that face
 *   onPick(item)
 *   menuLabel   the menu's accessible name
 *   stagger     ms between slabs (the geometry's own, else STAGGER_MS)
 *   selectedKey the item that is current: white border + label and a check
 *   hoverKey    the item under a sliding finger: filled with its accent, label black
 *   caption     a line of white mono caps beside the anchor (`SKIP BY · 7:20 NOTE`)
 */
export function FanLayer({ fan, items, origin = 'rail', scale = 1, face = null, anchorSize = 64, onPick, menuLabel = 'Start something', stagger = null, selectedKey = null, hoverKey = null, caption = null }) {
  const { mounted, expanded, rect, reduced, close, slabRefs } = fan;
  if (!mounted || !rect) return null;
  const geo = FAN_GEOMETRY[origin];
  const slabW = geo.width || SLAB_WIDTH;
  const staggerMs = stagger ?? geo.stagger ?? STAGGER_MS;
  const openAt = (i) => (typeof geo.open === 'function' ? geo.open(i) : geo.open[i]);
  const travel = (prop, delay) => reduced
    ? `${prop} 0ms linear 0ms`
    : expanded
      ? `${prop} ${OPEN_MS}ms cubic-bezier(0,0,0.2,1) ${delay}ms`
      : `${prop} ${CLOSE_MS}ms cubic-bezier(0.4,0,1,1) 0ms`;
  const maxLeft = (typeof window !== 'undefined' ? window.innerWidth : 1e4) - slabW - 8;

  return createPortal(
    // `display: contents` — no box, but pointer-events inherits through (.pr-overlay-root).
    <div className="pr-overlay-root" style={{ display: 'contents' }}>
      {/* Full-viewport, under the slabs and under the anchor itself. */}
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        data-fan-scrim
        onClick={() => close()}
        className="fixed inset-0 cursor-default"
        style={{
          zIndex: 9000,
          background: expanded ? 'rgba(0,0,0,0.60)' : 'rgba(0,0,0,0)',
          transition: reduced ? 'none' : 'background-color 120ms linear',
        }}
      />

      {caption && (
        <span
          aria-hidden
          data-fan-caption
          className="fixed font-mono font-bold whitespace-nowrap pointer-events-none"
          style={{ zIndex: 9001, left: rect.left + anchorSize + 12, top: rect.top + 2, color: '#fff', fontSize: 10, letterSpacing: '.12em', lineHeight: 1.5, opacity: expanded ? 1 : 0, transition: reduced ? 'none' : 'opacity 180ms linear' }}
        >
          {caption}
        </span>
      )}

      <div role="menu" aria-label={menuLabel}>
        {items.map((item, i) => {
          const at = expanded ? openAt(i) : geo.closed;
          const x = at.left * scale;
          const y = at.top * scale;
          const delay = expanded && !reduced ? i * staggerMs : 0;
          const selected = selectedKey != null && item.key === selectedKey;
          const hovered = hoverKey != null && item.key === hoverKey;
          const stroke = hovered ? item.accent : selected ? '#FFFFFF' : item.accent;
          return (
            <button
              key={item.key}
              data-portal-to={item.to}
              data-fan-item={item.key}
              data-selected={selected || undefined}
              data-hovered={hovered || undefined}
              ref={(el) => { slabRefs.current[i] = el; }}
              type="button"
              role={selectedKey != null ? 'menuitemradio' : 'menuitem'}
              aria-checked={selectedKey != null ? selected : undefined}
              tabIndex={expanded ? 0 : -1}
              onClick={() => onPick(item)}
              className="pr-fan-slab fixed text-left font-mono font-bold uppercase focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              style={{
                zIndex: 9001,
                // Never off either edge, wherever the anchor happens to sit.
                left: Math.max(8, Math.min(rect.left + x, maxLeft)),
                top: rect.top + y,
                width: slabW,
                minHeight: geo.height || 44,
                padding: geo.height ? '0 12px' : '13px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxSizing: 'border-box',
                lineHeight: '18px',
                fontSize: geo.height ? 11 : 10,
                letterSpacing: '0.10em',
                background: hovered ? item.accent : '#000000',
                color: hovered ? '#000000' : stroke,
                border: `2px solid ${stroke}`,
                opacity: expanded ? 1 : 0,
                pointerEvents: expanded ? 'auto' : 'none',
                transition: [
                  travel('left', delay),
                  travel('top', delay),
                  `opacity ${reduced ? 0 : (expanded ? OPEN_MS : CLOSE_MS)}ms linear ${delay}ms`,
                  'background-color 200ms',
                  'color 200ms',
                ].join(', '),
              }}
            >
              <span>{item.label}</span>
              {selected && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square" aria-hidden><path d="M4 12l5 5L20 6" /></svg>
              )}
            </button>
          );
        })}
      </div>

      {/* The anchor itself sits ON the scrim, inverted, its glyph turned into an ×. The real
          button keeps the focus underneath; this is the part you can see. */}
      {face && (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          data-fan-face
          onClick={() => close()}
          className="fixed rounded-full"
          style={{ zIndex: 9002, left: rect.left, top: rect.top, width: anchorSize, height: anchorSize, padding: 0, border: 0, background: 'transparent' }}
        >
          {face}
        </button>
      )}
    </div>,
    document.body,
  );
}
